import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import UpgradeRoomDetail from "./UpgradeRoomDetail";
import Navbar from "../Navbar/navbar";
import { baseUrl } from "../../Environments/environment";
import axios from "axios";
import { toast, ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Skeleton Loading Component
const RoomCardSkeleton = () => (
  <div className="w-full bg-gray-700 rounded-md p-4 animate-pulse">
    <div className="mb-2 rounded-md">
      <div className="w-full h-36 bg-gray-600 rounded-md"></div>
    </div>
    <div className="flex justify-between mb-2">
      <div className="h-6 bg-gray-600 rounded w-32"></div>
      <div className="h-6 bg-gray-600 rounded w-16"></div>
    </div>
    <div className="h-12 bg-gray-600 rounded mb-2"></div>
    <div className="h-10 bg-gray-600 rounded mt-4"></div>
  </div>
);

const UpgradeRoom = ({
  upgradeRooms,
  onUpgradeRoomSelect,
  propertyMobile,
  propertyLogo,
}) => {
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidated, setIsValidated] = useState(false);

  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const guestSessionId = params.id;
  const { guest, upgradeRoomRequests } = location.state || {};

  // Check if there's any active upgrade room request (not declined)
  const hasActiveUpgradeRequest = upgradeRoomRequests && upgradeRoomRequests.some(
    (request) => request.requestStatus !== "Declined"
  );

  // Get the active request to check room type
  const activeRequest = upgradeRoomRequests && upgradeRoomRequests.find(
    (request) => request.requestStatus !== "Declined"
  );

  // Helper function to check if a room is the same as the active request
  const isRoomAlreadyRequested = (room) => {
    return activeRequest && activeRequest.roomType === room.id;
  };

  // Validation: Redirect if guest or roomType is not present
  useEffect(() => {
    if (!guest || !guest.roomType) {
      console.log("Guest or roomType not present, redirecting to previous page");
      navigate(-1); // Go back to previous page
      return;
    }
    setIsValidated(true); // Mark as validated if guest and roomType are present
  }, [guest, navigate]);

  useEffect(() => {
    // Simulate loading delay
    const loadRooms = async () => {
      console.log("guest", guest);
      setIsLoading(true);
      fetchRooms(guest.roomType);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsLoading(false);
    };

    loadRooms();
  }, []);

  const fetchRooms = async (roomId) => {
    try {
        console.log("Room Id -> ", roomId);
        const propertyId = guest.propertyId
      const response = await axios.get(
        `${baseUrl}/upgradeRoomWorkflow/roomTypeWithPriority/${propertyId}`
      );
      console.log("Res -> ",response)
      if(response.data.statusCode === 200){
        console.log("Response -> ", response.data.result);
        const validRooms = response.data.result.roomType.filter((room) => room.priority < guest.roomType);
        setRooms(validRooms);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    setIsDetailOpen(true);
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
    setSelectedRoom(null);
  };

    const handleFinalRoomSelection = async (room) => {
        setIsSubmitting(true);
        
        try {
            // API call to submit upgrade room request
            const response = await axios.post(
                `${baseUrl}/guestApi/${guestSessionId}/upgradeRoomRequest`,
                {
                    "name": "Upgrade Room",
                    "requestType":"Upgrade Room",
                    "roomType": room.id,
                    "price": room.price
                }
            );

            if (response.status === 200 || response.status === 201) {
                // Success toast
                toast.success("Upgrade room request submitted successfully!", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    transition: Bounce,
                });
                
                // Call the parent's onUpgradeRoomSelect function
                if (onUpgradeRoomSelect) {
                    onUpgradeRoomSelect(room);
                }
                
                handleDetailClose();
            } else {
                throw new Error("Request failed");
            }
        } catch (error) {
            console.error("Upgrade room request error:", error);
            
            // Error toast
            toast.error(
                error.response?.data?.message || 
                "Failed to submit upgrade room request. Please try again.",
                {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    transition: Bounce,
                }
            );
        } finally {
            setIsSubmitting(false);
        }
    };

  // Don't render the component if validation hasn't passed
  if (!isValidated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Validating access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Sticky Navbar */}
      <div className="sticky top-0 z-40">
        <Navbar
          first={guest?.firstName}
          last={guest?.lastName}
          propertyMobile={propertyMobile}
          propertyLogo={propertyLogo}
        />
      </div>

      {/* Main Content */}
      <div className="p-4 rounded-lg shadow-lg text-white w-full flex-shrink-0">
        <div className="flex flex-row justify-between items-center w-[100%]">
          <div className="text-[#FFFFFF] font-poppins font-light text-[22px]">
            Upgrade Room Options
          </div>
        </div>
        <div className="mt-4">
          <div className="space-y-4">
            {isLoading ? (
              // Show skeleton loading
              <>
                <RoomCardSkeleton />
                <RoomCardSkeleton />
                <RoomCardSkeleton />
              </>
            ) : (
              // Show actual room cards
              rooms.map((room) => (
                <div
                  key={room._id || room.id}
                  className="w-full bg-gray-700 rounded-md p-4 transform transition-transform hover:scale-105"
                >
                  <div className="mb-2 rounded-md">
                    <img
                      src={
                        room.images[0] ||
                        "https://st3.depositphotos.com/1022597/35608/i/450/depositphotos_356083076-stock-photo-grey-stone-texture-useful-background.jpg"
                      }
                      alt={room.name}
                      className="w-full h-36 object-cover rounded-md"
                    />
                  </div>
                  <div className="flex justify-between mb-2">
                    <div className="text-lg font-semibold">{room.roomType}</div>
                    <div className="text-lg font-semibold">
                      {room.price && room.price > 0 ? `$${room.price}` : ""}
                    </div>
                  </div>
                  {room.description && (
                    <div className="text-gray-300 mb-2 text-sm h-12 overflow-hidden">
                      {room.description.length > 50
                        ? `${room.description.slice(0, 50)}...`
                        : `${room.description}.`}
                    </div>
                  )}
                  <button
                    className={`max-w-sm mx-auto mt-4 flex flex-row items-center justify-center w-full border-2 border-gray-50 py-2 rounded-lg shadow-md focus:outline-none focus:shadow-outline ${
                      hasActiveUpgradeRequest 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-indigo-600 text-white hover:bg-indigo-400'
                    }`}
                    onClick={() => handleRoomSelect(room)}
                    disabled={hasActiveUpgradeRequest}
                  >
                    {isRoomAlreadyRequested(room) 
                      ? 'Request Already Sent' 
                      : hasActiveUpgradeRequest 
                        ? 'Select Room' 
                        : 'Select Room'
                    }
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mb-6 mt-12 text-gray-50 text-sm">
          Powered By Onelyk.Com
          <div className="mt-2 space-x-4">
            <a href="/terms" className="text-blue-400 hover:underline">
              Terms and Conditions
            </a>
            <a href="/privacy" className="text-blue-400 hover:underline">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      {/* Room Detail Modal */}
      <UpgradeRoomDetail
        isOpen={isDetailOpen}
        onClose={handleDetailClose}
        room={selectedRoom}
        onRoomSelect={handleFinalRoomSelection}
        isSubmitting={isSubmitting}
        hasActiveUpgradeRequest={hasActiveUpgradeRequest}
        activeRequest={activeRequest}
      />
      
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

export default UpgradeRoom;
